import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FluxiLogo from "../assets/svg/FluxiLogo.svg";
import { getMenuItems } from "../const/menuItems";
import { SidebarItem } from "./SidebarItem";
import { dictionary } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";
import { Chip } from "./common/chip/Chip";
import { ModalWrapper } from "./modals/ModalWrapper";
import { BuyCreditsModal } from "./modals/BuyCreditsModal";
import { membershipStatsAxios } from "../services/membership/MembershipAxios";
import { handleApi } from "../utils/handleApi";

const Sidebar = ({ isTutorial }: { isTutorial: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [indicatorOffset] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [mascotCopyIndex, setMascotCopyIndex] = useState(() =>
    Math.floor(Math.random() * 20)
  );
  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [endMembershipDate, setEndMembershipDate] = useState("");
  const [loadingCredits, setLoadingCredits] = useState(false);
  const path = location.pathname;
  const { language } = useAuth();
  const menuItems = getMenuItems();
  const mascotCopies = [
    "Psst... tu embudo me dijo que lo extrañas.",
    "No me mires así, yo no fui el que abandonó el checkout.",
    "Tranqui, tus clientes no se van a ir solos... ah, espera.",
    "Yo aquí trabajando y tú pasando el mouse por encima mío.",
    "Si cada vez que me tocas fuera una venta, ya serías millonario.",
    "Tu competencia acaba de publicar una página. Solo digo.",
    "Prometo no juzgarte si no has tocado tu embudo en 3 días.",
    "Tengo más energía que tu página de ventas un lunes.",
    "Dato curioso: los embudos también tienen sentimientos.",
    "Esta es tu señal para dejar de pasear y vender algo.",
    "Yo no duermo. Tu embudo tampoco. El único flojo aquí eres tú.",
    "Mi terapeuta dice que deje de cargar tus ventas. No le hice caso.",
    "Dicen que el dinero no da la felicidad. Pero un buen embudo sí.",
    "¿Dejaste el checkout sin botón de compra otra vez, verdad?",
    "Mientras lees esto, alguien cerró la pestaña de tu página.",
    "Soy un zorro de IA. Optimizar es literalmente lo mío.",
    "Tu embudo tiene más potencial que tu lista de tareas pendientes.",
    "No es por presionar, pero tus métricas me están mirando feo.",
    "¿El cliente que perdiste ayer volvió? Es broma, no volvió.",
    "Descansa tranquilo, yo cuido tus ventas. Bueno, lo intento.",
  ];

  // Precargar imagenes de la mascota para evitar parpadeo
  useEffect(() => {
    const imgs = [
      "https://fluxi.co/prod/assets/images/fluxi-sidebar-default.webp",
      "https://fluxi.co/prod/assets/images/fluxi-sidebar-hover.webp",
    ];
    imgs.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Rotar copy cada vez que se hace hover
  useEffect(() => {
    if (isHovered) {
      setMascotCopyIndex((prev) => (prev + 1) % mascotCopies.length);
    }
  }, [isHovered]);

  const handleBuyCredits = () => {
    setIsOpenBuyCreditsModal(true);
  };

  const handleMenuItemClick = (item: (typeof menuItems)[number]) => {
    if (item.path === "/training") {
      item.onClick?.();
      return;
    }

    navigate(item.path);
  };

  const closeModal = () => {
    setIsOpenBuyCreditsModal(false);
  };

  useEffect(() => {
    setLoadingCredits(true);
    const getStats = async () => {
      const { res } = await handleApi(membershipStatsAxios(), false);
      if (res) {
        if (!res.active_membership) {
          setLoadingCredits(false);
          return;
        }
        setUserCredits(res.active_membership.credits);
        setEndMembershipDate(res.active_membership.ends_at);
      }
      setLoadingCredits(false);
    };
    getStats();
  }, []);

  return (
    <>
      {isOpenBuyCreditsModal && (
        <ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={closeModal}>
          <BuyCreditsModal
            onClose={closeModal}
            nextRenewalDate={endMembershipDate}
            language={language}
          />
        </ModalWrapper>
      )}
      <div
        className="w-60 h-screen bg-[#FFFFFF] border-r border-gray-200 flex flex-col fixed left-0 top-0"
        style={{ zIndex: 2 }}
      >
        <div
          className="p-4 cursor-pointer"
          onClick={() =>
            isTutorial ? () => { } : navigate("/home")
          }
        >
          <img src={FluxiLogo} alt="Fluxi Logo" className="pl-3 h-auto" />
        </div>

        <nav className="sidebar-nav flex-1 overflow-y-auto">
          <div className="flex flex-col justify-between h-full">
            {/* Active indicator */}
            <div className="relative">
              <div
                className="absolute left-0 w-1 h-12 bg-[#EBEEFF] rounded-r-xl transition-all duration-200"
                style={{
                  transform: `translateY(${indicatorOffset}px)`,
                  opacity: indicatorOffset >= 0 ? 1 : 0,
                }}
              />

              {/* Main menu items */}
              <div className="menu-section px-4 space-y-1">
                {menuItems
                  .filter((item) => item.section === "main")
                  .map((item) => (
                    <SidebarItem
                      key={item.path}
                      icon={item.icon}
                      text={dictionary[item.text]?.[language] || item.text}
                      path={item.path}
                      active={location.pathname === item.path || (item.path === "/ads" && location.pathname.startsWith("/ads/"))}
                      disabled={
                        (location.pathname === "/plans" || isTutorial) ? true : item.disabled
                      }
                      onClick={() => handleMenuItemClick(item)}
                    />
                  ))}
              </div>

              {/* Settings section */}
              <div className="menu-section px-4 space-y-1">
                <div className="mx-4 border-t border-[#EAEAEA] mt-2"></div>
                {menuItems
                  .filter((item) => item.section === "settings")
                  .map((item) => (
                    <SidebarItem
                      key={item.path}
                      icon={item.icon}
                      text={dictionary[item.text]?.[language] || item.text}
                      path={item.path}
                      active={location.pathname === item.path}
                      disabled={isTutorial ? true : item.disabled}
                      onClick={() => navigate(item.path)}
                    />
                  ))}
              </div>

            </div>

            <div className="relative px-4 mt-auto overflow-visible">
              {/* Globo de texto */}
              <div
                className={`
                  absolute bottom-full left-1/2 -translate-x-1/2 mb-4 w-[75%]
                  transition-all duration-200 ease-in-out
                  ${isHovered
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-2 pointer-events-none"
                  }
                `}
              >
                <div
                  className="relative px-3 py-2.5 rounded-xl"
                  style={{
                    background: "white",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <p className="text-sm leading-snug font-semibold text-gray-700 text-center">
                    {mascotCopies[mascotCopyIndex]}
                  </p>
                  {/* Puntica del globo */}
                  <div
                    className="absolute -bottom-[6px] left-1/2 -translate-x-1/2"
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "7px solid transparent",
                      borderRight: "7px solid transparent",
                      borderTop: "7px solid white",
                      filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.04))",
                    }}
                  />
                </div>
              </div>

              {path !== "/home" && (
                <div
                  className="relative w-full"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <img
                    src="https://fluxi.co/prod/assets/images/fluxi-sidebar-default.webp"
                    alt="Fluxi Mascot"
                    className={`w-full transition-opacity duration-200 ease-in-out ${isHovered ? "opacity-0" : "opacity-100"}`}
                  />
                  <img
                    src="https://fluxi.co/prod/assets/images/fluxi-sidebar-hover.webp"
                    alt="Fluxi Mascot Excited"
                    className={`w-full absolute inset-0 transition-opacity duration-200 ease-in-out ${isHovered ? "opacity-100" : "opacity-0"}`}
                  />
                </div>
              )}
              {path == "/home" && (
                <div className="flex justify-center mb-6">
                  <Chip
                    text={dictionary["credits"][language]}
                    value={userCredits.toString()}
                    onClick={handleBuyCredits}
                    loading={loadingCredits}
                  />
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
